import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { kbApi } from '../api/kb';
import { aiApi } from '../api/ai';
import { useCompany } from '../contexts/CompanyContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Search, FileText, Sparkles, Plus, Globe, Lock, BookOpen } from 'lucide-react';
import type { KnowledgeArticle } from '../types';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';

type VisibilityTab = 'all' | 'external' | 'internal';

export default function KnowledgeBase() {
  const { currentCompany } = useCompany();
  const { can, isStaff } = usePermissions();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [useAISearch, setUseAISearch] = useState(false);
  const [visibilityTab, setVisibilityTab] = useState<VisibilityTab>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const companyKey = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const standardQuery = useQuery({
    queryKey: ['kb', search, category, page, visibilityTab, statusFilter, companyKey],
    queryFn: () => kbApi.list({
      search: search || undefined,
      category: category || undefined,
      page,
      limit: 20,
      visibility: isStaff && visibilityTab !== 'all' ? visibilityTab : undefined,
      status: isStaff && statusFilter ? statusFilter : undefined,
    }),
    enabled: !!currentCompany && (!useAISearch || search.length <= 3),
  });

  const aiSearchQuery = useQuery({
    queryKey: ['kb-ai', search, companyKey],
    queryFn: () => aiApi.kbMatch(search),
    enabled: !!currentCompany && useAISearch && search.length > 3,
  });

  const useAIResults = useAISearch && search.length > 3;
  const data = useAIResults ? {
    articles: aiSearchQuery.data ?? [],
    total: (aiSearchQuery.data?.length ?? 0),
    page: 1,
    limit: 20,
  } : standardQuery.data;
  const isLoading = useAIResults ? aiSearchQuery.isLoading : standardQuery.isLoading;
  const error = useAIResults ? aiSearchQuery.error : standardQuery.error;

  const articles = (data?.articles ?? []) as KnowledgeArticle[];
  const total = data?.total ?? 0;
  const pages = data ? Math.ceil(data.total / data.limit) : 1;

  const articleId = (a: KnowledgeArticle) => (a as any).id ?? (a as any)._id;
  const authorName = (a: KnowledgeArticle & { author?: { name?: string } }) => (a as any).author?.name ?? 'Unknown';

  if (!currentCompany) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="card p-6">
          <EmptyState icon={<BookOpen className="w-10 h-10" />}>
            Select a company to view the knowledge base.
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Knowledge Base"
        intro="Search published help articles, or use AI search to find semantically related answers. Staff can filter by visibility and status."
        actions={
          can('create', 'knowledgeBase') ? (
            <Link to="/kb/new" className="btn btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Article
            </Link>
          ) : undefined
        }
      />

      {/* Visibility tabs — staff only */}
      {isStaff && (
        <div className="flex gap-1 p-1 bg-row-odd border border-line-light rounded-lg w-fit">
          {(['all', 'external', 'internal'] as VisibilityTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => { setVisibilityTab(tab); setPage(1); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                visibilityTab === tab
                  ? 'bg-surface text-fg shadow-sm'
                  : 'text-muted hover:text-fg'
              }`}
            >
              {tab === 'external' ? <Globe className="w-3.5 h-3.5" /> : tab === 'internal' ? <Lock className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />}
              <span className="capitalize">{tab}</span>
            </button>
          ))}
        </div>
      )}

      <div className="card p-4 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="search"
              placeholder="Search articles..."
              className="input pl-9"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <button
            type="button"
            className={`btn btn-ghost text-sm inline-flex items-center gap-2 ${useAISearch ? 'bg-primary-muted text-primary' : ''}`}
            onClick={() => setUseAISearch(!useAISearch)}
            title="Use AI to find semantically related articles"
          >
            <Sparkles className="w-4 h-4" />
            AI search
          </button>
          {!useAISearch && (
            <input
              type="text"
              placeholder="Category filter"
              className="input w-40"
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            />
          )}
          {isStaff && !useAISearch && (
            <select
              className="select w-36"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          )}
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-muted">Loading articles...</div>
        ) : error ? (
          <div className="py-12 text-center text-err">Failed to load articles</div>
        ) : articles.length === 0 ? (
          <EmptyState icon={<FileText className="w-10 h-10" />}>
            No articles found.{' '}
            {can('create', 'knowledgeBase')
              ? <>Click <strong>New Article</strong> to create your first one.</>
              : 'Try a different search or category.'}
          </EmptyState>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((article) => (
                <Link
                  key={articleId(article)}
                  to={`/kb/${article.slug}`}
                  className="block p-4 rounded-lg border border-line hover:border-primary hover:bg-row-odd transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-medium text-fg line-clamp-2">{article.title}</h3>
                    {article.visibility === 'internal' && (
                      <span title="Internal article"><Lock className="w-3.5 h-3.5 text-warn shrink-0 mt-0.5" /></span>
                    )}
                  </div>
                  {article.excerpt && (
                    <p className="mt-1 text-sm text-muted line-clamp-2">{article.excerpt}</p>
                  )}
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted flex-wrap">
                    <span className={`badge ${
                      article.status === 'published'
                        ? 'bg-ok/15 text-ok'
                        : 'bg-row-odd text-muted'
                    }`}>
                      {article.status}
                    </span>
                    {article.category && (
                      <span className="text-primary">{article.category}</span>
                    )}
                    <span>{authorName(article)}</span>
                    <span>· {article.viewCount} views</span>
                  </div>
                </Link>
              ))}
            </div>

            {pages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-line">
                <p className="text-sm text-muted">
                  Page {page} of {pages} ({total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    className="btn btn-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-secondary"
                    disabled={page >= pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
